import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { ComboBox, DropDownList } from "@progress/kendo-react-dropdowns";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { useQueryGetSystemMenuFilters } from "../../data/services/benefitPlanService";
import { SERVICE_URLS } from "../../data/helpers/serviceUrls";
import TerminatedBadge, { terminationLabel, TERMINATED_TOOLTIP, TERMINATED_COLOR } from "../../components/TerminatedBadge";
import axios from "axios";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import OEContext from "../../layouts/OEContext";
// Debounce utility
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  
  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};


const planTypes = ["Medical", "Dental", "Vision"];

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
const getLocalISODate = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

// Collapse a value to local midnight of its calendar day.
//
// The two date sources here parse with different semantics: the Effective Date
// input yields "2026-11-01" (date-only => parsed as UTC midnight), while
// /api/OEPeriod yields "2026-11-01T00:00:00" (no offset => parsed as local
// midnight). At a negative UTC offset the API value lands hours *after* the
// input value for the same calendar day, so a period's own start date compares
// as greater than itself. Normalizing both sides makes them equal.
export const toLocalDay = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  // Not an ISO-ish string; fall back to Date parsing, but reject values like
  // null/false that Date would happily coerce to the epoch.
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// Order OE periods for a target effective date: the period covering the date
// first (latest start on or before it), then earlier periods newest-first, then
// future periods oldest-first as a fallback.
export const rankPeriodsByEffectiveDate = (periods, dateStr) => {
  const targetDate = toLocalDay(dateStr);
  if (!targetDate) return [];

  const sorted = (Array.isArray(periods) ? periods : [])
    .map(p => ({ ...p, effectiveDate: toLocalDay(p?.effectiveDate) }))
    .filter(p => p.effectiveDate)
    .sort((a, b) => a.effectiveDate - b.effectiveDate);

  return [
    ...[...sorted].reverse().filter(p => p.effectiveDate <= targetDate),
    ...sorted.filter(p => p.effectiveDate > targetDate),
  ];
};
const usePctFormatter = () =>
  useMemo(
    () => new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 0 }),
    []
  );

const API = "".replace(/\/+$/, "");
const fetchJson = async (path, opts = {}) => {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { Accept: "application/json", ...(opts.headers || {}) },
  });
  if (!res.ok) {
    let body = "";
    try {
      body = await res.text();
    } catch {}
    const err = new Error(`HTTP ${res.status}${body ? `: ${body}` : ""}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json();
};





export default function ContributionModelBuilderWithGate() {
  const [lockedPlanType, setLockedPlanType] = useState(null); 

  return (
    <div style={{ padding: 24 }}>
      <h2>Contribution Model Builder</h2>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <span style={{ fontSize: 14, color: "#555" }}>
          Plan Type: <strong>{lockedPlanType || "— (select to continue)"}</strong>
        </span>
        {lockedPlanType && (
          <Button fillMode="outline" size="small" onClick={() => setLockedPlanType(null)}>
            Change Plan Type
          </Button>
        )}
      </div>

      {lockedPlanType === null && (
        <Dialog title="Select a Plan Type" onClose={() => {}}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, width: 420 }}>
            <p style={{ margin: 0, color: "#666" }}>
              Choose one plan type to model. You can’t mix Medical, Dental, and Vision in the same
              grid.
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {planTypes.map((t) => (
              <Button
                key={t}
                themeColor="primary"
                onClick={() => setLockedPlanType(t)}
                style={{ minWidth: 120 }}
                //disabled={t !== "Medical" && t !== "Dental"}   
              >
                {t}
              </Button>
            ))}
            </div>
          </div>
          <DialogActionsBar>
            <span style={{ fontSize: 12, color: "#999", marginRight: 8 }}>
              Selection is required to continue.
            </span>
          </DialogActionsBar>
        </Dialog>
      )}

      {lockedPlanType && <ContributionModelBuilder lockedPlanType={lockedPlanType} />}
    </div>
  );
}


function ContributionModelBuilder({ lockedPlanType }) {
  const oePeriod = React.useContext(OEContext);

  const rowSeq = useRef(1);
  const gridPlanTypeRef = useRef(lockedPlanType ?? null); 
  const [allowNoPlansMessage, setAllowNoPlansMessage] = useState(false);
  const pctFmt = usePctFormatter();
  const fmtPct = (v) => pctFmt.format(v ?? 0);
  const excelExportRef = useRef(null);
  const [clientName, setClientName] = useState("");
  // SOF-754: real client identity drives termed-plan filtering/validation.
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientFilter, setClientFilter] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(() => getLocalISODate());

  // SOF-754: load the client master list for the searchable picker.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setClientsLoading(true);
        const res = await fetch(SERVICE_URLS.CLIENT_MASTER_LIST, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (active) setClients(Array.isArray(data) ? data : []);
      } catch {
        if (active) setClients([]);
      } finally {
        if (active) setClientsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const selectedClient = useMemo(
    () => clients.find((c) => c.clientId === clientId) || null,
    [clients, clientId]
  );

  const filteredClients = useMemo(() => {
    const q = (clientFilter || "").trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      `${c.clientName || ""} ${c.clientId || ""}`.toLowerCase().includes(q)
    );
  }, [clients, clientFilter]);

  const [planType, setPlanType] = useState(lockedPlanType);
  useEffect(() => {
    setPlanType(lockedPlanType);
  }, [lockedPlanType]);

  const [carrier, setCarrier] = useState(null); // store full object now
  const [planName, setPlanName] = useState("");
  const [planId, setPlanId] = useState("");
  const [planDetails, setPlanDetails] = useState([]);
  // NEW: Rate group dropdown state
const [rateGroups, setRateGroups] = useState([]);
const [selectedRateGroup, setSelectedRateGroup] = useState(null);
const [rateGroupsLoading, setRateGroupsLoading] = useState(false);
const [rateGroupsError, setRateGroupsError] = useState(null);
// const [oePeriodId, setOePeriodId] = useState(oePeriod.oePeriodId);
const [systemMenuFilters, setSystemMenuFilters] = useState([])

  const [contributionType, setContributionType] = useState("Percent");
  const [modelData, setModelData] = useState([]);
  const [modelId, setModelId] = useState(null);

  // SOF-754: rows flagged termed by the backend (on load) block saving until removed (AC3).
  const hasTermedRows = useMemo(
    () => modelData.some((r) => r.isTermed),
    [modelData]
  );

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");

  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [modelsList, setModelsList] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState("");
  const [selectedModelKey, setSelectedModelKey] = useState("");
  const [modelsSearch, setModelsSearch] = useState("");

  const [selectedIds, setSelectedIds] = useState(new Set());

  const [carriers, setCarriers] = useState([]);
  const [carriersLoading, setCarriersLoading] = useState(false);
  const [carriersError, setCarriersError] = useState("");

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState("");

  const [adding, setAdding] = useState(false);

  const [excelLoading, setExcelLoading] = useState(false);

  const PLAN_TYPE_CODES = {
    Medical: "MED",
    Dental: "DEN",
    Vision: "VIS"
  };


  useEffect(() => {
  if (plansLoading) {
    setAllowNoPlansMessage(false);
    const timer = setTimeout(() => {
      setAllowNoPlansMessage(true);
    }, 1000); 

    return () => clearTimeout(timer);
  }
}, [plansLoading]);


  useEffect(() => {
    const ctrl = new AbortController();
    setCarrier(null);
    setPlanName("");
    setPlanId("");
    setCarriers([]);
    setCarriersError("");   
    setRateGroups([]);    
    setSelectedRateGroup(null);    
    setRateGroupsError("");

    if (!planType) return () => ctrl.abort();

    (async () => {
      setCarriersLoading(true);
      try {
        const res = await fetch(
          `${API}/api/carrier/by-plan-type/${encodeURIComponent(planType)}`,
          { signal: ctrl.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setCarriers(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.name !== "AbortError") {
          setCarriersError("Unable to load carriers.");
        }
      } finally {
        setCarriersLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [planType]);


useEffect(() => {
  if (!carrier || !planType) {
    setPlans([]);
    setPlansError("");
    return;
  }

  setPlans([]);
  setPlansError("");
  setPlansLoading(true);

  (() => {
    try {
     let plans = []
     let loadedPlans = {}
      for (const p of planDetails) {
        if (loadedPlans[p.planId]) continue;
        if (p.benefitGroup.trim() === carrier.carrierName.trim()) {
          loadedPlans[p.planId] = true;
          plans.push({
            planId: p.planId,
            planName: `${p.planId} - ${p.planDesc}`,
            // SOF-798: termed plans are now returned flagged, shown greyed/read-only.
            isTermed: p.isTermed === true,
            terminationType: p.terminationType || null,
          });
        }
      }
      setPlans(
        Array.isArray(plans)
          ? plans
          : []
      );
    } catch (e) {
      setPlansError("Unable to load plans.");
    } finally {
      setPlansLoading(false);
    }
  })();
}, [carrier, planType, effectiveDate]); 

const processPlansData = (data) => {
  if (!Array.isArray(data)) return;
  setPlanDetails(data);
  let carriers = []
  let carrierMap = {}

  for (const p of data) {
    if (carrierMap[p.benefitGroup.trim()]) continue;
    if (p.offerTypeCode === 'MED' && planType === 'Medical') {
      carriers.push({carrierId: p.benefitGroup, carrierName: p.benefitGroup});
      carrierMap[p.benefitGroup.trim()] = true;
    } else if (p.offerTypeCode === 'DEN' && planType === 'Dental') {
      carriers.push({carrierId: p.benefitGroup, carrierName: p.benefitGroup});
      carrierMap[p.benefitGroup.trim()] = true;
    } else if (p.offerTypeCode === 'VIS' && planType === 'Vision') {
      carriers.push({carrierId: p.benefitGroup, carrierName: p.benefitGroup});
      carrierMap[p.benefitGroup.trim()] = true;
    }
  }
  setCarriers(carriers);
  // setCarriers(carriers);
}

const getOePeriodIdFromDate = async (dateStr) => {
  try {
    const res = await fetch(`${API}/api/OEPeriod`);
    if (!res.ok) throw new Error(`Failed to fetch OE Periods (HTTP ${res.status})`);
    const list = await res.json();

    const match = rankPeriodsByEffectiveDate(list, dateStr)[0];

    if (!match) {
      return null;
    }

      // SOF-754: pass clientId so eliminated/termed plans are excluded server-side.
      const plans = await fetch(SERVICE_URLS.SYSTEM_PLAN_DETAILS(match.oePeriodId, clientId));
      if (!plans.ok) throw new Error(`HTTP ${plans.status}`);
      processPlansData(await plans.json());
    return match.oePeriodId;
  } catch (e) {
    return null;
  }
};

// SOF-754: (re)load the plan/carrier list whenever the client or effective
// date changes. When a real client is picked, clientId scopes the response so
// termed plans are excluded (AC2). For free-form clients (not in the master
// list) clientId is empty and the backend returns the unfiltered plan list.
useEffect(() => {
  if (!effectiveDate) return;
  getOePeriodIdFromDate(effectiveDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [effectiveDate, clientId]);

const fetchRateGroupsForPlan = async (pid, dateStr) => {
  const res = await fetch(`${API}/api/OEPeriod`);
  if (!res.ok) throw new Error("Failed to fetch OE Periods");
  const allPeriods = await res.json();

  const candidates = rankPeriodsByEffectiveDate(allPeriods, dateStr);

  for (const p of candidates) {
    const data = await fetchJson(
      `/api/BenefitPlan/rates?oePeriodId=${p.oePeriodId}&planId=${encodeURIComponent(pid)}`
    );

    if (Array.isArray(data) && data.length > 0) {
      const uniq = Array.from(
        new Set(
          data
            .map(x => String(x.RateGroup ?? x.rateGroup ?? x.RateGroupName ?? "").trim())
            .filter(Boolean)
        )
      );
      return uniq;
    }
  }

  return [];
};

const handleGenerateExcel = async () => {
  try {
    setExcelLoading(true); // 👈 start loading
    const oePeriodId = await getOePeriodIdFromDate(effectiveDate);
    if (!oePeriodId) {
      alert("Could not resolve OE Period for the selected date.");
      return;
    }

    const safe = (s) =>
      (s || "").trim().replace(/[^a-z0-9_\-]+/gi, "_").replace(/^_+|_+$/g, "");

    const computedKey =
      (typeof modelId === "string" && modelId.trim()) ||
      `${safe(clientName) || "Client"}-${planType}-${effectiveDate}`;

    const payload = {
      Key: computedKey,
      ClientName: (clientName || "").trim(),
      ClientId: clientId, // SOF-754: lets the backend reject termed plans on export
      EffectiveDate: effectiveDate,
      PlanType: planType,
      ContributionType: contributionType,
      SelectedRateGroup: selectedRateGroup,
      OEPeriodId: oePeriodId, // 👈 FIX: include OEPeriodId

      CarrierJson: carrier
        ? { CarrierId: carrier.carrierId, CarrierName: carrier.carrierName }
        : null,

      Rows: modelData.map((r, idx) => ({
        Key: `${safe(clientName)}|${r.planId}|${r.rateGroup}|${r.tier}|${effectiveDate}#${idx + 1}`,
        PlanId: r.planId,
        PlanName: r.planName,
        Carrier: r.carrier,
        RateGroup: r.rateGroup,
        Tier: r.tier,
        TotalPremium: parseFloat(r.totalPremium || 0),
        EmployerPercent: parseFloat(r.employerPercent || 0),
        EmployeePercent: parseFloat(r.employeePercent || 0),
        EmployerDollar: parseFloat(r.employerDollar || 0),
        EmployeeDollar: parseFloat(r.employeeDollar || 0),
      })),
    };

    const res = await axios.post(
      `${API}/api/contributionmenu/from-grid-excel`,
      payload,
      { responseType: "blob", headers: { "Content-Type": "application/json" } }
    );

    const cd = res.headers?.["content-disposition"];
    let filename = `PrestigePEO-${safe(clientName) || "Client"}-${planType}-Menu.xlsx`;
    if (cd) {
      const m = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i);
      if (m) filename = decodeURIComponent(m[1] || m[2] || filename);
    }

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    const serverMsg =
      err?.response?.data && typeof err.response.data !== "string"
        ? JSON.stringify(err.response.data)
        : err?.response?.data || err.message;
    alert(`Failed to generate Excel menu.\n${serverMsg}`);
  } finally {
    setExcelLoading(false); // 👈 stop loading
  }
};


const onPlanPick = async (item) => {
  const pid = item?.planId || "";
  setPlanId(pid);
  setPlanName(item?.planName || "");
  setRateGroups([]);
  setSelectedRateGroup(null);
  setRateGroupsError("");

  if (!pid) return;

  try {
    setRateGroupsLoading(true);
    
     let rates = []
     let loadedRates = {}
      for (const p of planDetails) {
        if (loadedRates[p.rateGroup]) continue;
        if (p.benefitGroup === carrier.carrierName && p.planId === pid) {
          loadedRates[p.rateGroup] = true;
          rates.push(p.rateGroup);
        }
      }
    setRateGroups(rates);
    if (rates.length === 1) setSelectedRateGroup(rates[0]);
  } catch (e) {
    setRateGroupsError("Unable to load billing rate groups.");
  } finally {
    setRateGroupsLoading(false);
  }
};

  useEffect(() => {
    (async () => {
      if (!planId) return;
      try {
        setRateGroupsLoading(true);
        const groups = await fetchRateGroupsForPlan(planId, effectiveDate);
        setRateGroups(groups);
        if (!groups.includes(selectedRateGroup)) setSelectedRateGroup(groups[0] || "");
      } catch (e) {
        setRateGroupsError("Unable to load billing rate groups.");
      } finally {
        setRateGroupsLoading(false);
      }
    })();

  }, [effectiveDate]);


const fetchTotalsByPlanId = async ({ planId, effectiveDate, rateGroup }) => {
  const res = await fetch(`${API}/api/OEPeriod`);
  if (!res.ok) throw new Error(`Failed to fetch OE Periods (HTTP ${res.status})`);
  const allPeriods = await res.json();

  const candidates = rankPeriodsByEffectiveDate(allPeriods, effectiveDate);

  const num = (o, ...k) => {
    for (const x of k) {
      const v = o?.[x];
      if (v != null && !Number.isNaN(Number(v))) return Number(v);
    }
    return 0;
  };
  const str = (o, ...k) => {
    for (const x of k) {
      const v = o?.[x];
      if (v != null && String(v).trim() !== "") return String(v);
    }
    return "—";
  };

  for (const p of candidates) {
    const groups = await fetchJson(
      `/api/BenefitPlan/rates?oePeriodId=${p.oePeriodId}&planId=${encodeURIComponent(planId)}`
    );

    const list = Array.isArray(groups) ? groups : [];
    if (list.length === 0) continue;

    let pick = rateGroup
      ? list.find(g => (g.RateGroup ?? g.rateGroup) === rateGroup)
      : list.find(
          g =>
            num(g, "EEAmount", "eeAmount") > 0 ||
            num(g, "ESAmount", "esAmount") > 0 ||
            num(g, "ECAmount", "ecAmount") > 0 ||
            num(g, "FAMAmount", "famAmount") > 0
        ) || list[0];

    if (!pick) {
      continue;
    }

    const rateGroupName = str(pick, "RateGroup", "RateGroupName", "rateGroup");

    const result = {
      rateGroup: rateGroupName,
      rows: [
        { tier: "EE", totalPremium: num(pick, "EEAmount", "eeAmount") },
        { tier: "EE + SP", totalPremium: num(pick, "ESAmount", "esAmount") },
        { tier: "EE + CH", totalPremium: num(pick, "ECAmount", "ecAmount") },
        { tier: "Family", totalPremium: num(pick, "FAMAmount", "famAmount") },
      ],
    };

    return result;
  }

  throw new Error(
    `No billing rates found for plan ${planId} (checked ${candidates.length} OEPeriods)`
  );
};

const buildPdfPayload = () => {

  const planMap = new Map();

  modelData.forEach((row) => {
    if (!planMap.has(row.planId)) {
      planMap.set(row.planId, {
        PlanName: row.planName,
        Carrier: row.carrier,
        RateGroup: row.rateGroup,
        EECount: 0,
        ESCount: 0,
        ECCount: 0,
        FAMCount: 0,
        EECost: null,
        ESCost: null,
        ECCost: null,
        FAMCost: null,

      });
    }

    const planObj = planMap.get(row.planId);

    switch (row.tier) {
      case "EE":
        planObj.EECost = row.totalPremium.toFixed(2);
        planObj.EECount += 1;
        break;
      case "EE + SP":
        planObj.ESCost = row.totalPremium.toFixed(2);
        planObj.ESCount += 1;
        break;
      case "EE + CH":
        planObj.ECCost = row.totalPremium.toFixed(2);
        planObj.ECCount += 1;
        break;
      case "Family":
        planObj.FAMCost = row.totalPremium.toFixed(2);
        planObj.FAMCount += 1;
        break;
      default:
        break;
    }
  });


  return {
    Title: `${clientName} - ${planType} Benefit Menu`,
    SubTitle: `${carrier?.carrierName || ""} | Rate Group: ${selectedRateGroup || ""}`,
    EffectiveDateString: effectiveDate,
    Footer: "Confidential and Proprietary",
    NewBenefitRateGroups: selectedRateGroup,
    Plans: Array.from(planMap.values()),
  };
};

const downloadPdfMenu = async () => {
  const payload = buildPdfPayload();

  const res = await fetch("/api/BenefitPlan/systemmenu/from-grid", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    alert("Failed to generate PDF");
    return;
  }

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${clientName}-${planType}-Menu.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
};

  const normalizePercent = (v) =>
    v == null || Number.isNaN(v) ? 0 : v > 1 ? clamp(v / 100, 0, 1) : clamp(v, 0, 1);

const handleAddPlan = async () => {
  if (!planId || !planName || !carrier || !planType || !effectiveDate || adding) return;

  // SOF-798: defensive guard – refuse to add a termed plan even if onChange was bypassed.
  if (plans.find((p) => p.planId === planId)?.isTermed) return;

  if (!selectedRateGroup) {
    alert("Please choose a Billing Rate Group.");
    return;
  }


  if (!gridPlanTypeRef.current && modelData.length === 0)
    gridPlanTypeRef.current = planType;
  if (gridPlanTypeRef.current !== planType) {
    alert(`You can’t mix plan types in the same grid. Current grid type is ${gridPlanTypeRef.current}.`);
    return;
  }
  setAdding(true);
  try {
    const result = await fetchTotalsByPlanId({ planId, effectiveDate, rateGroup: selectedRateGroup });


    const totals = result?.rows || [
      { tier: "EE", totalPremium: 0 },
      { tier: "EE + SP", totalPremium: 0 },
      { tier: "EE + CH", totalPremium: 0 },
      { tier: "Family", totalPremium: 0 },
    ];

    setModelData((prev) => {
      const seen = new Set(
        prev.map((r) => `${r.carrier}|${r.planId}|${r.rateGroup}|${r.tier}|${effectiveDate}`)
      );
      const toAdd = totals
        .map((r) => {
          const key = `${carrier.carrierName}|${planId}|${selectedRateGroup}|${r.tier}|${effectiveDate}`;
          if (seen.has(key)) return null;

          const total = r.totalPremium || 0;
          const locked = planType === "Vision" && carrier.carrierName?.toLowerCase().includes("funded");
          return {
            id: `${key}#${rowSeq.current++}`,
            carrier: carrier.carrierName,
            planId,
            planName,
            rateGroup: selectedRateGroup,
            tier: r.tier,
            totalPremium: total,
            employerPercent: 1,
            employeePercent: 0,
            employerDollar: total,
            employeeDollar: 0,
            locked,
          };
        })
        .filter(Boolean);

      return [...prev, ...toAdd];
    });
  } catch (e) {
    alert(e?.message || "Unable to load rates. Check API and try again.");
  } finally {
    setAdding(false);
  }
};


  const recalcFromPercent = (row, changed, value) => {
    const p = clamp(Number(value) || 0, 0, 1);
    const total = row.totalPremium || 0;
    const employerPercent = changed === "employerPercent" ? p : 1 - p;
    const employeePercent = 1 - employerPercent;
    const employerDollar = round2(employerPercent * total);
    const employeeDollar = round2(total - employerDollar);
    return { employerPercent, employeePercent, employerDollar, employeeDollar };
  };
  const recalcFromDollar = (row, changed, value) => {
    const total = row.totalPremium || 0;
    const v = clamp(Number(value) || 0, 0, total);
    const employerDollar = changed === "employerDollar" ? v : round2(total - v);
    const employeeDollar = round2(total - employerDollar);
    const employerPercent = total ? employerDollar / total : 0;
    const employeePercent = 1 - employerPercent;
    return { employerPercent, employeePercent, employerDollar, employeeDollar };
  };
  const handleChange = useCallback((value, id, field) => {
    setModelData((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        if (row.locked) return row;
        const next = { ...row };
        if (field === "employerPercent" || field === "employeePercent") {
          Object.assign(next, recalcFromPercent(row, field, normalizePercent(value)));
        } else if (field === "employerDollar" || field === "employeeDollar") {
          Object.assign(next, recalcFromDollar(row, field, value));
        }
        return next;
      })
    );
  }, []);

  // Debounced version for percent fields
  const debouncedHandleChange = useDebounce(handleChange, 700);


  const [menusOpen, setMenusOpen] = useState(false);
  const [menusBusy, setMenusBusy] = useState(false);
  const [menusError, setMenusError] = useState("");


  const [menusPlans, setMenusPlans] = useState([]);
  const uniquePlansFromGrid = useCallback(() => {
    const m = new Map();
    modelData.forEach((r) => {
      if (!m.has(r.planId))
        m.set(r.planId, { planId: r.planId, planName: r.planName, carrier: r.carrier });
    });
    return Array.from(m.values());
  }, [modelData]);


  const [planToBG, setPlanToBG] = useState(new Map());

  const [bgToRateGroups, setBgToRateGroups] = useState(new Map());

  const OFFER_CODE = { Medical: "MED", Dental: "DEN", Vision: "VIS" }[planType] || "MED";

const openGenerateMenus = async () => {
  setMenusError("");
  setMenusBusy(true);
  setMenusOpen(true);

  try {
    const chips = uniquePlansFromGrid();
    setMenusPlans(chips);

    if (chips.length === 0) {
      setMenusError("No plans selected from the grid.");
    }
  } catch (e) {
    setMenusError(e.message || "Unable to prepare system menus.");
  } finally {
    setMenusBusy(false);
  }
};

  const removeMenuPlan = (pid) =>
    setMenusPlans((prev) => prev.filter((p) => p.planId !== pid));

  const downloadBlob = (blob, fallbackName = "BenefitMenus.pdf") => {
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

const onDownloadSystemMenu = async () => {
  if (modelData.length === 0) {
    setMenusError("No plans in grid.");
    return;
  }
  if (!selectedRateGroup) {
    setMenusError("Please select a Billing Rate Group first.");
    return;
  }

  setMenusBusy(true);
  setMenusError("");

  try {
    const oePeriodId = await getOePeriodIdFromDate(effectiveDate);
    if (!oePeriodId) throw new Error("Could not resolve OE Period for the selected date.");


    const payload = {
      oePeriodId,
      offerTypeCode: OFFER_CODE,
      clientName,
      effectiveDate,
      selectedRateGroup,
      plans: modelData.map(r => ({
        planId: r.planId,
        planName: r.planName,
        carrier: r.carrier,
        rateGroup: r.rateGroup,
        tier: r.tier,
        totalPremium: r.totalPremium,
        employerPercent: r.employerPercent,
        employeePercent: r.employeePercent,
        employerDollar: r.employerDollar,
        employeeDollar: r.employeeDollar,
      })),
    };

    const res = await fetch(`${API}/api/contributionmenu/from-grid`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error(await res.text());

    const cd = res.headers.get("Content-Disposition") || "";
    let filename = `PrestigePEO-${planType}-Comparison.pdf`;
    const m = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i);
    if (m) filename = decodeURIComponent(m[1] || m[2] || filename);

    const blob = await res.blob();
    downloadBlob(blob, filename);

    setMenusOpen(false);
  } catch (e) {
    setMenusError(e.message || "Failed to generate comparison menu.");
  } finally {
    setMenusBusy(false);
  }
};

/** save/load/delete (unchanged APIs) */
const openSaveDialog = () => {
  if (!modelId) setSaveName("");
  setShowSaveDialog(true);
};

const confirmSave = async () => {
  // Require a client name (picked from the master list OR entered free-form).
  // When picked from the list, clientId is also populated and the backend runs
  // its SOF-754 termed-plan validation; free-form entries save without it.
  if (!clientName?.trim()) {
    alert("Please enter a Client before saving.");
    return;
  }
  // SOF-754 (AC3): don't allow saving while eliminated/termed plans remain.
  if (hasTermedRows) {
    alert("This model contains eliminated/termed plan(s). Please remove the highlighted rows before saving.");
    return;
  }

  const model = {
    id: modelId || null,
    saveName: saveName?.trim(),
    clientName,
    clientId,
    effectiveDate,
    planType,
    rows: modelData.map((r, i) => ({
      //id: i + 1,
      carrier: r.carrier,
      planId: r.planId,
      rateGroup: r.rateGroup,
      tier: r.tier,
      totalPremium: r.totalPremium,
      employerPercent: r.employerPercent,
      employerDollar: r.employerDollar,
      employeePercent: r.employeePercent,
      employeeDollar: r.employeeDollar,
    })),
  };

  try {
    await axios.post("/api/contribution", model);
    setShowSaveDialog(false);
  } catch (err) {
    // SOF-754 (AC4): surface the server-side validation message.
    const msg = err?.response?.data?.error || "Failed to save model";
    alert(msg);
  }
};

const openLoadDialog = async () => {
  const res = await axios.get("/api/contribution");
  const normalized = res.data.map(m => ({
    ...m,
    key: m.key || m.id  // fallback to id if key missing
  }));
  setModelsList(normalized);
  setShowLoadDialog(true);
};
const filteredModels = useMemo(() => {
  const q = (modelsSearch || "").trim().toLowerCase();
  if (!q) return modelsList;
  return modelsList.filter((m) => {
    const t = [m.id || "", m.clientName || "", m.effectiveDate || "", m.planType || ""]
      .join(" ")
      .toLowerCase();
    return t.includes(q);
  });
}, [modelsList, modelsSearch]);

const confirmLoadDialog = async () => {
  const res = await axios.get(`/api/contribution/${encodeURIComponent(selectedModelKey)}`);
  const model = res.data;
  setClientName(model.clientName);
  setClientId(model.clientId || ""); // SOF-754
  setSaveName(model.saveName || "");
  setEffectiveDate(model.effectiveDate);
  setPlanType(model.planType);
  // SOF-754: rows arrive with isTermed flags from the backend guardrail (AC3).
  setModelData(model.rows);
  setModelId(model.id);
  setShowLoadDialog(false);
};

const onDeleteModel = async () => {
  if (!modelId) {
    alert("No model loaded.");
    return;
  }
  if (!window.confirm(`Delete model '${modelId}'?`)) return;
  try {
    const res = await fetch(`/api/contribution/${encodeURIComponent(modelId)}`, {
      method: "DELETE",
    });
    if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
    setModelId(null);
    setModelData([]);
    setSelectedIds(new Set());
    alert("Deleted.");
  } catch {
    alert("Delete failed.");
  }
};

  const toggleRow = (id) =>
    setSelectedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const onDeleteRows = () => {
    if (selectedIds.size === 0) return;
    setModelData((prev) => prev.filter((r) => !selectedIds.has(r.id)));
    setSelectedIds(new Set());
  };

  let tabIndex = 1;
const PercentCell = (props) => {
  const [tempValue, setTempValue] = useState(props.field === 'employerPercent' ? props.dataItem.employerPercent : props.dataItem.employeePercent);
  const locked = props.dataItem.locked;

  // SOF-826: carrier-terminated plans are informational comparison records only. Their
  // contribution cells are read-only (never editable) and are not used in any calculation.
  if (props.dataItem.isTermed) {
    return <td style={{ color: TERMINATED_COLOR }}>Not Available</td>;
  }

  return (
    <td>
      <NumericTextBox
        value={tempValue}
        format="p0"
        step={0.01}
        min={0}
        max={1}
        disabled={locked}
        onChange={(e) => {
          setTempValue(e.value ?? 0);
        }}
        onBlur={() => {
          // Submit the value on blur, using the local state, which is always the latest input
          handleChange(tempValue, props.dataItem.id, props.field);
        }}
      />
    </td>
  );
};

const FixedCell = (props) => {
  const [tempValue, setTempValue] = useState(props.field === 'employeeDollar' ? props.dataItem.employeeDollar : props.dataItem.employerDollar);
  const locked = props.dataItem.locked;

  // SOF-826: carrier-terminated plans are informational comparison records only. Their
  // contribution cells are read-only (never editable) and are not used in any calculation.
  if (props.dataItem.isTermed) {
    return <td style={{ color: TERMINATED_COLOR }}>Not Available</td>;
  }

  return (
    <td>
      <NumericTextBox
        value={tempValue}
        format="c"
        step={5}
        min={0}
        max={props.dataItem.totalPremium}
        disabled={locked}
        onChange={(e) => {
          setTempValue(e.value ?? 0);
        }}
        onBlur={() => {
          // Submit the value on blur, using the local state, which is always the latest input
          handleChange(tempValue, props.dataItem.id, props.field);
        }}
      />
    </td>
  );
};
  return (
    <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
          marginBottom: 20,
          alignItems: "end",
        }}
      >
        {/* Client (SOF-754: searchable picker with free-form fallback for clients
            that aren't in the master list). ComboBox allowCustom lets the user
            type any name; clientId only gets populated when a known client is
            picked, so termed-plan filtering runs automatically for those. */}
        <div style={{ display: "flex", flexDirection: "column", width: 320 }}>
          <label className="k-label">Client {clientsLoading ? "(loading…)" : ""}</label>
          <ComboBox
            data={filteredClients}
            textField="clientName"
            dataItemKey="clientId"
            // Kendo ComboBox with dataItemKey needs an object shape to render
            // the display text. Wrap a free-form name so it shows in the input
            // instead of falling back to the placeholder.
            value={
              selectedClient ||
              (clientName ? { clientName, clientId: "" } : null)
            }
            allowCustom={true}
            filterable={true}
            onFilterChange={(e) => setClientFilter(e.filter.value)}
            onChange={(e) => {
              const v = e.value;
              if (v && typeof v === "object") {
                setClientId(v.clientId || "");
                setClientName(v.clientName || "");
              } else if (typeof v === "string") {
                setClientName(v);
                // Free-form entry has no id in the master list; clear any
                // stale id so we don't send a mismatched pair downstream.
                setClientId("");
              } else {
                setClientId("");
                setClientName("");
              }
              setClientFilter("");
            }}
            placeholder="Search or enter client…"
          />
        </div>

        {/* Effective Date */}
        <div style={{ display: "flex", flexDirection: "column", width: 200 }}>
          <label className="k-label">Effective Date</label>
          <input
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="k-input"
            style={{ height: 36, padding: "0 10px" }}
          />
        </div>

        {/* Plan Type (locked) */}
        <div style={{ display: "flex", flexDirection: "column", width: 200 }}>
          <label className="k-label">Plan Type</label>
          <div
            style={{
              height: 36,
              display: "flex",
              alignItems: "center",
              padding: "0 10px",
              border: "1px solid #ddd",
              borderRadius: 4,
              background: "#fafafa",
              fontWeight: 600,
            }}
            title="This model is locked to a single plan type"
          >
            {planType}
          </div>
        </div>

        {/* Carrier */}
        <div style={{ display: "flex", gap: 20 }}>
        <div style={{ display: "flex", flexDirection: "column", width: 220 }}>
        <label className="k-label">Carrier {carriersLoading ? "(loading…)" : ""}</label>
        <DropDownList
          data={carriers}
          textField="carrierName"
          dataItemKey="carrierId"
          value={carrier}
          onChange={(e) => {
            setCarrier(e.value || null);
            setPlanId("");
            setPlanName("");
          }}
          defaultItem={{ carrierId: "", carrierName: "Select a Carrier" }}  
        />
        {carriersError && <span style={{ color: "crimson", fontSize: 12 }}>{carriersError}</span>}
        {!carriersLoading && !carriersError && carriers.length === 0 && (
        <span style={{ fontSize: 12 }}>No carriers available.</span>
        )}
        </div>


      <div style={{ display: "flex", flexDirection: "column", width: 380 }}>
  <label className="k-label">Plan {plansLoading ? "(loading…)" : ""}</label>
  <DropDownList
    data={plans}
    textField="planName"
    dataItemKey="planId"
    value={plans.find((p) => p.planId === planId) || null}
    onChange={(e) => {
      // SOF-798: termed plans are shown but cannot be selected/added.
      if (e.value?.isTermed) return;
      onPlanPick(e.value);
    }}
    itemRender={(li, itemProps) => {
      const item = itemProps.dataItem;
      if (!item?.isTermed) return li;
      // Grey out + append the terminated label; block pointer selection.
      return React.cloneElement(
        li,
        {
          ...li.props,
          title: TERMINATED_TOOLTIP,
          style: { ...li.props.style, color: TERMINATED_COLOR, fontStyle: "italic", cursor: "not-allowed" },
          onClick: (ev) => ev.stopPropagation(),
        },
        <span>
          {item.planName} <span style={{ marginLeft: 6 }}>{terminationLabel(item.terminationType)}</span>
        </span>
      );
    }}
    defaultItem={{ planId: "", planName: plansLoading ? "Loading plans…" : "Select a Plan" }}
    disabled={!carrier || plansLoading || !!plansError}
  />
  
  {/* Error message */}
  {plansError && (
    <span style={{ color: "crimson", fontSize: 12 }}>{plansError}</span>
  )}

  {/* Only show 'No plans' AFTER loading is finished */}
{!plansLoading && !plansError && allowNoPlansMessage && carrier && plans.length === 0 && (
  <span style={{ fontSize: 12 }}>
    No plans found for {carrier.carrierName}.
  </span>
)}
</div>
        </div>


        {/* Billing Rate Group */}
        <div style={{ display: "flex", flexDirection: "column", width: 320 }}>
          <label className="k-label">Billing Rate Group {rateGroupsLoading ? "(loading…)" : ""}</label>
          <DropDownList
            data={rateGroups}
            value={selectedRateGroup}
            onChange={(e) => setSelectedRateGroup(e.value)}
            defaultItem={"Select a Billing Rate Group"}
            disabled={!planId || rateGroupsLoading || !!rateGroupsError}
          />
          {rateGroupsError && (
            <span style={{ color: "crimson", fontSize: 12 }}>{rateGroupsError}</span>
          )}
          {!rateGroupsLoading && !rateGroupsError && planId && rateGroups.length === 0 && (
            <span style={{ fontSize: 12 }}>No rate groups found for this plan.</span>
          )}
        </div>

        {/* Add */}
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <Button
            themeColor="primary"
            onClick={handleAddPlan}
            disabled={
              adding ||
              !planId ||
              !planName ||
              !carrier ||
              !planType ||
              !effectiveDate ||
              !selectedRateGroup
            }
            style={{
              height: "38px",
              fontSize: "14px",
              padding: "0 16px",
              marginLeft: "8px",
              verticalAlign: "middle",
            }}
          >
            {adding ? "Adding..." : "Add Plan"}
          </Button>
        </div>

        {/* Contribution Type */}
        <div style={{ width: "100%" }} />
        <div style={{ display: "flex", flexDirection: "column", width: 200 }}>
          <label className="k-label">Contribution Type</label>
          <DropDownList
            data={["Percent", "Fixed"]}
            value={contributionType}
            onChange={(e) => setContributionType(e.value)}
          />
        </div>
      </div>

      {/* SOF-754 (AC3): guardrail banner when eliminated/termed plans are present */}
      {hasTermedRows && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: "10px 14px",
            border: "1px solid #f5c2c7",
            background: "#f8d7da",
            color: "#842029",
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          This model contains eliminated/termed plan(s), highlighted below. Remove
          them before saving. Saving is disabled until they are removed.
        </div>
      )}

      {/* Grid wrapped in ExcelExport */}
<ExcelExport
  ref={excelExportRef}
  data={modelData}
  fileName={`ContributionGrid-${clientName || "Client"}-${planType}-${effectiveDate}.xlsx`}
>
  <Grid
    data={modelData}
    dataItemKey="id"
    style={{ marginTop: 8 }}
    rowRender={(row, props) => {
      // SOF-826: carrier-terminated plans render greyed out and read-only, shown for
      // historical comparison only (informational, not participating in calculations).
      if (!props.dataItem?.isTermed) return row;
      const style = { ...row.props.style, background: "#f2f2f2", color: TERMINATED_COLOR };
      return React.cloneElement(row, { ...row.props, style }, row.props.children);
    }}
  >
    <GridColumn
      title=""
      width="50px"
      cell={(props) => (
        <td>
          <input
            type="checkbox"
            checked={selectedIds.has(props.dataItem.id)}
            onChange={() => toggleRow(props.dataItem.id)}
          />
        </td>
      )}
    />
    <GridColumn
      title="Status"
      width="170px"
      cell={(props) => (
        <td>
          {props.dataItem.isTermed ? (
            <TerminatedBadge terminationType={props.dataItem.terminationType} style={{ marginLeft: 0 }} />
          ) : (
            <span style={{ color: "#0f5132" }}>Active</span>
          )}
        </td>
      )}
    />
    <GridColumn field="carrier" title="Carrier" />
    <GridColumn field="planId" title="Plan ID" />
    <GridColumn field="rateGroup" title="Billing Rate Group" />
    <GridColumn field="tier" title="Tier" />
    <GridColumn field="totalPremium" title="Total Premium" format="{0:c}" />

    <GridColumn
  field="employerPercent"
  title="Employer %"
  cell={(props) =>
    contributionType === "Percent"
      ? <PercentCell dataItem={props.dataItem} field="employerPercent" />
      : <td>{fmtPct(props.dataItem.employerPercent)}</td>
  }
/>

    <GridColumn
      field="employerDollar"
      title="Employer $"
      format="{0:c}"
      cell={(props) =>
        contributionType === "Fixed" ? <FixedCell dataItem={props.dataItem} field="employerDollar"  /> : (
          <td>
            {props.dataItem.employerDollar?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }) || "$0.00"}
          </td>
        )
      }
    />

    <GridColumn
      field="employeePercent"
      title="Employee %"
      cell={(props) =>
        contributionType === "Percent" ? <PercentCell dataItem={props.dataItem} field="employeePercent"  /> : (
          <td>{fmtPct(props.dataItem.employeePercent)}</td>
        )
      }
    />

    <GridColumn
      field="employeeDollar"
      title="Employee $"
      format="{0:c}"
      cell={(props) =>
        contributionType === "Fixed" ? <FixedCell dataItem={props.dataItem} field="employeeDollar"  /> : (
          <td>
            {props.dataItem.employeeDollar?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }) || "$0.00"}
          </td>
        )
      }
    />
  </Grid>
</ExcelExport>

{/* Buttons */}
<div style={{ display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
  <Button
    themeColor="primary"
    onClick={openSaveDialog}
    disabled={hasTermedRows}
    title={hasTermedRows ? "Remove eliminated/termed plans before saving" : undefined}
  >
    Save Model
  </Button>
  <Button fillMode="outline" onClick={openLoadDialog}>
    Load Model
  </Button>
  <Button fillMode="outline" onClick={onDeleteModel} disabled={!modelId}>
    Delete Model
  </Button>
  <Button fillMode="outline" onClick={onDeleteRows} disabled={modelData.length === 0}>
    Delete Selected Rows
  </Button>

  <Button
    themeColor="secondary"
    onClick={() => excelExportRef.current?.save()}
    disabled={modelData.length === 0}
  >
    Export Grid Data (Excel)
  </Button>
<Button
  themeColor="secondary"
  onClick={() => {
    if (!clientName?.trim()) {
      alert("Please enter a Client Name");
      return;
    }
    if (modelData.length === 0) {
      alert("Please add at least one plan to the grid");
      return;
    }
    // SOF-754 (AC5): don't export a menu that still contains termed plans.
    if (hasTermedRows) {
      alert("This model contains eliminated/termed plan(s). Please remove the highlighted rows before generating the menu.");
      return;
    }
    handleGenerateExcel();
  }}
  disabled={excelLoading || hasTermedRows}
>
  {excelLoading ? "Generating Excel…" : "Generate Rate Menus (Excel)"}
</Button>
</div>

      {!!modelId && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#555" }}>
          Current Model ID: <code>{modelId}</code>
        </div>
      )}

      {/* Save dialog */}
      {showSaveDialog && (
        <Dialog onClose={() => setShowSaveDialog(false)} title={"Save Contribution Grid"}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, width: 420 }}>
            <label className="k-label">Enter a name / ID for this grid</label>
            <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="" />
          </div>
          <DialogActionsBar>
            <Button onClick={() => setShowSaveDialog(false)}>Cancel</Button>
            <Button themeColor="primary" onClick={confirmSave}>
              Save
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Load dialog */}
      {showLoadDialog && (
        <Dialog onClose={() => setShowLoadDialog(false)} title={"Load Saved Model"}>
          <div style={{ width: 760, maxWidth: "90vw", display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Input
                value={modelsSearch}
                onChange={(e) => setModelsSearch(e.target.value)}
                placeholder="Search by Model ID, Client, Effective date, or Plan Type"
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 12, color: "#666" }}>
                {filteredModels.length} result{filteredModels.length === 1 ? "" : "s"}
              </span>
            </div>

            {modelsLoading && <div>Loading…</div>}
            {modelsError && <div style={{ color: "crimson" }}>{modelsError}</div>}

            {!modelsLoading && !modelsError && (
              <div style={{ maxHeight: "50vh", overflow: "auto" }}>
                <table className="k-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th>Model ID</th>
                      <th>Client</th>
                      <th>Effective</th>
                      <th style={{ textAlign: "right", paddingRight: "30px" }}>Rows</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredModels.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ padding: 12, color: "#666" }}>
                          No saved models.
                        </td>
                      </tr>
                    )}
                    {filteredModels.map((m) => (
                      <tr key={m.key}>
                        <td>
                          <input
                            type="radio"
                            name="modelPick"
                            checked={selectedModelKey === m.key}
                            onChange={() => setSelectedModelKey(m.key)}
                          />
                        </td>
                        <td>
                          <code>{m.key}</code>
                        </td>
                        <td>{m.clientName}</td>
                        <td>{m.effectiveDate}</td>
                        <td style={{ textAlign: "right", paddingRight: "30px" }}>{m.rowCount}</td>
                        <td>{m.updatedUtc ? new Date(m.updatedUtc).toLocaleString() : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <DialogActionsBar>
            <Button onClick={() => setShowLoadDialog(false)}>Cancel</Button>
            <Button themeColor="primary" disabled={!selectedModelKey} onClick={confirmLoadDialog}>
              Load Selected
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Generate System Rate Menus dialog (chips only) */}
      {menusOpen && (
        <Dialog onClose={() => !menusBusy && setMenusOpen(false)} title={`${planType} Benefit Filter Menus`}>
          <div style={{ width: 760, maxWidth: "90vw", display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Chips / tags (plans from grid) */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", minHeight: 36 }}>
              {menusPlans.map((p) => (
                <span
                  key={p.planId}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 10px",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    background: "#f7f7f7",
                    fontSize: 13,
                  }}
                  title={p.planName}
                >
                  <span style={{ fontFamily: "monospace" }}>{p.planId}</span>
                  <span style={{ color: "#666" }}>{p.planName}</span>
                  <button
                    type="button"
                    onClick={() => removeMenuPlan(p.planId)}
                    disabled={menusBusy}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      color: "#666",
                      fontSize: 14,
                      lineHeight: 1,
                    }}
                    aria-label={`Remove ${p.planId}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              {menusPlans.length === 0 && <span style={{ color: "#999" }}>No plans selected.</span>}
            </div>

            {!!menusError && <div style={{ color: "crimson" }}>{menusError}</div>}
            {menusBusy && <div>Working…</div>}
          </div>

          <DialogActionsBar>
            <Button themeColor="primary" onClick={onDownloadSystemMenu} disabled={menusBusy || menusPlans.length === 0}>
              {menusBusy ? "Preparing…" : "Download Selected"}
            </Button>
            <Button onClick={() => setMenusOpen(false)} disabled={menusBusy}>
              Close
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
}